// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, Type } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { BaseModalComponent, BaseModalConfig } from '@lfx/shared/components/base-modal';
import { ConfirmationModalComponent } from '@lfx/shared/components/popups/confirmation/confirmation.component';
import {
  confirmRejectDialogueOptions,
  addWorkspaceModalOptions,
} from '@lfx/config/modal-config';

export interface FormModalConfig extends BaseModalConfig {
  formComponent: Type<any>;
  formInputs?: { [key: string]: any };
  formOutputs?: { [key: string]: (value: any) => void };
}

export interface ConfirmationConfig {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'primary';
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  constructor(private ngbModal: NgbModal) {}

  /**
   * Create a simple confirmation modal
   */
  confirm(config: ConfirmationConfig): Observable<boolean> {
    return new Observable(observer => {
      const modalRef = this.ngbModal.open(
        ConfirmationModalComponent,
        confirmRejectDialogueOptions
      );

      modalRef.componentInstance['title'] = config.title;
      modalRef.componentInstance['message'] = config.message;
      modalRef.componentInstance['confirmLabel'] = config.confirmLabel || 'Confirm';
      modalRef.componentInstance['rejectLabel'] = config.cancelLabel || 'Cancel';

      modalRef.componentInstance.confirmAction.subscribe(() => {
        observer.next(true);
        observer.complete();
        modalRef.dismiss();
      });

      modalRef.componentInstance.rejectAction.subscribe(() => {
        observer.next(false);
        observer.complete();
        modalRef.dismiss();
      });

      modalRef.dismissed.subscribe(() => {
        observer.next(false);
        observer.complete();
      });
    });
  }

  /**
   * Create a form modal using the base modal wrapper
   */
  openForm<T = any>(config: FormModalConfig, options?: NgbModalOptions): Observable<T | null> {
    return new Observable(observer => {
      const modalOptions: NgbModalOptions = {
        ...addWorkspaceModalOptions,
        size: config.size || 'sm',
        ...options
      };

      // Set backdrop behavior based on config
      if (config.allowBackdropClose === false) {
        modalOptions.backdrop = 'static';
      }

      const modalRef = this.ngbModal.open(BaseModalComponent, modalOptions);

      // Configure the base modal
      modalRef.componentInstance.config = config;

      // Handle confirm action
      modalRef.componentInstance.confirmed.subscribe(() => {
        // The form component should handle the actual submission
        // and call closeWithResult when done
      });

      // Handle modal result
      modalRef.result.then(
        (result: T) => {
          observer.next(result);
          observer.complete();
        },
        () => {
          observer.next(null);
          observer.complete();
        }
      );
    });
  }

  /**
   * Open any custom component as a modal
   */
  openComponent<T = any>(
    component: Type<any>,
    config?: {
      inputs?: { [key: string]: any },
      options?: NgbModalOptions
    }
  ): Observable<T | null> {
    return new Observable(observer => {
      const modalRef = this.ngbModal.open(component, config?.options || addWorkspaceModalOptions);

      // Set inputs if provided
      if (config?.inputs) {
        Object.keys(config.inputs).forEach(key => {
          modalRef.componentInstance[key] = config.inputs![key];
        });
      }

      modalRef.result.then(
        (result: T) => {
          observer.next(result);
          observer.complete();
        },
        () => {
          observer.next(null);
          observer.complete();
        }
      );
    });
  }

  /**
   * Quick confirmation for dangerous actions
   */
  confirmDanger(title: string, message: string): Observable<boolean> {
    return this.confirm({
      title,
      message,
      confirmLabel: 'Yes, Delete',
      cancelLabel: 'Cancel',
      variant: 'danger'
    });
  }

  /**
   * Quick info modal (OK only)
   */
  info(title: string, message: string): Observable<void> {
    return new Observable(observer => {
      const modalRef = this.ngbModal.open(
        ConfirmationModalComponent,
        confirmRejectDialogueOptions
      );

      modalRef.componentInstance['title'] = title;
      modalRef.componentInstance['message'] = message;
      modalRef.componentInstance['confirmLabel'] = 'OK';
      modalRef.componentInstance['rejectLabel'] = '';

      modalRef.componentInstance.confirmAction.subscribe(() => {
        observer.next();
        observer.complete();
        modalRef.dismiss();
      });

      modalRef.dismissed.subscribe(() => {
        observer.next();
        observer.complete();
      });
    });
  }
}
